<template>
  <div v-if="ready && enabled" class="ai-helper-wrap" :class="{ 'is-mobile': isMobile }">
    <!-- 活动小球 (屏幕右侧, 可上下拖动; 拖动结束位移<6px 视为点击打开抽屉) -->
    <div
      v-if="!drawerVisible"
      class="ai-ball"
      :class="{ dragging: dragging }"
      :style="{ top: ballTop + 'px' }"
      :title="'AI 助手 · ' + configName"
      @mousedown.prevent="onDragStart"
      @touchstart.prevent="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onTouchEnd"
    >
      <span class="ai-ball-icon">🌾</span>
      <span class="ai-ball-label">AI<br />助手</span>
    </div>

    <!-- 右侧抽屉对话 (v-if + CSS animation: 不依赖 transitionend, 后台标签页也不会卡住) -->
    <div v-if="drawerVisible" class="ai-drawer" role="dialog" aria-label="AI 助手对话">
      <div class="ai-drawer-header">
        <div class="ai-drawer-title">
          <span class="ai-drawer-logo">🌾</span>
          <div>
            <div class="ai-drawer-name">粮小助 · AI 客服</div>
            <div class="ai-drawer-env">{{ configName }}</div>
          </div>
        </div>
        <a-icon type="close" class="ai-drawer-close" @click="drawerVisible = false" />
      </div>
      <div class="ai-drawer-body">
        <!-- iframe 懒加载: 第一次打开才渲染, 之后保活(隐藏不销毁, 对话不中断) -->
        <iframe
          v-if="iframeLoaded"
          ref="frame"
          :src="chatUrl"
          class="ai-drawer-frame"
          title="粮小助 AI 问答"
          allow="microphone; clipboard-write"
          @load="onFrameLoad"
        />
      </div>
    </div>

    <!-- 遮罩 (仅窄屏) -->
    <div v-if="drawerVisible && isMobile" class="ai-mask" @click="drawerVisible = false" />
  </div>
</template>

<script>
import { AI_HELPER_FALLBACK, AI_HELPER_ENABLED, buildChatUrl } from '@/constants/aiHelper'
import { fetchActiveAiHelperConfig } from '@/api/dryingAiHelper'
import { installAiJump } from './aiJumpLinks'

const BALL_TOP_KEY = 'ai-helper-ball-top'
/** 球可停留的上下安全边距 (避开顶栏/底栏) */
const TOP_MARGIN = 64
const BOTTOM_MARGIN = 90

/**
 * AiHelperBall — 全局 AI 客服悬浮球 + 右侧抽屉 (挂 MainLayout, 登录后所有页面可见).
 *
 * 来源: 后端 TBL_DRYING_AI_HELPER_CONFIG 激活行; 拉不到走本地兜底 (演示/后端未发版).
 * 球支持上下拖动 (记住位置, localStorage), 位移<6px 视为点击打开抽屉.
 * 抽屉内嵌 MaxKB 聊天页 (mode=mobile), iframe 懒加载 + 保活, 切页签/收起不丢会话.
 */
export default {
  name: 'AiHelperBall',
  data() {
    return {
      enabled: AI_HELPER_ENABLED,
      ready: false,
      config: AI_HELPER_FALLBACK,
      drawerVisible: false,
      iframeLoaded: false,
      isMobile: window.innerWidth < 768,
      ballTop: Math.round(window.innerHeight * 0.4),
      dragging: false,
      aiJumpCleanup: null
    }
  },
  computed: {
    configName() {
      return this.config.configName || 'AI 助手'
    },
    chatUrl() {
      return buildChatUrl(this.config)
    }
  },
  created() {
    this.resolveConfig()
    this.restoreBallTop()
    window.addEventListener('resize', this.onResize)
    window.addEventListener('mousemove', this.onDragMove)
    window.addEventListener('mouseup', this.onDragEnd)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('mousemove', this.onDragMove)
    window.removeEventListener('mouseup', this.onDragEnd)
    if (this.aiJumpCleanup) {
      this.aiJumpCleanup()
      this.aiJumpCleanup = null
    }
  },
  methods: {
    /** 后端激活配置优先, 失败/为空走本地兜底 */
    resolveConfig() {
      fetchActiveAiHelperConfig()
        .then(cfg => {
          if (cfg) this.config = cfg
        })
        .catch(() => {})
        .finally(() => {
          this.ready = true
        })
    },
    /** ---------- 拖动 (鼠标) ---------- */
    onDragStart(e) {
      this.startDrag(e.clientY)
    },
    onDragMove(e) {
      this.moveDrag(e.clientY)
    },
    onDragEnd() {
      this.endDrag()
    },
    /** ---------- 拖动 (触屏) ---------- */
    onTouchStart(e) {
      this.startDrag(e.touches[0].clientY)
    },
    onTouchMove(e) {
      this.moveDrag(e.touches[0].clientY)
    },
    onTouchEnd() {
      this.endDrag()
    },
    startDrag(clientY) {
      this.dragging = true
      this.dragState = { startY: clientY, originTop: this.ballTop, moved: false }
    },
    moveDrag(clientY) {
      if (!this.dragging || !this.dragState) return
      const dy = clientY - this.dragState.startY
      if (Math.abs(dy) > 5) this.dragState.moved = true
      if (!this.dragState.moved) return
      this.ballTop = this.clampTop(this.dragState.originTop + dy)
    },
    endDrag() {
      if (!this.dragging) return
      const moved = this.dragState && this.dragState.moved
      this.dragging = false
      this.dragState = null
      if (moved) {
        try { localStorage.setItem(BALL_TOP_KEY, String(this.ballTop)) } catch (e) { /* 隐私模式忽略 */ }
      } else {
        // 位移极小 = 点击意图
        this.openDrawer()
      }
    },
    clampTop(top) {
      const max = window.innerHeight - BOTTOM_MARGIN
      return Math.min(Math.max(top, TOP_MARGIN), Math.max(max, TOP_MARGIN))
    },
    restoreBallTop() {
      try {
        const saved = parseInt(localStorage.getItem(BALL_TOP_KEY), 10)
        if (!isNaN(saved)) this.ballTop = this.clampTop(saved)
        else this.ballTop = this.clampTop(this.ballTop)
      } catch (e) { /* localStorage 不可用则用默认 */ }
    },
    openDrawer() {
      this.drawerVisible = true
      this.iframeLoaded = true
    },
    /**
     * [2026-09-28] iframe 每次真实加载(抽屉重建)都重新安装「回答内菜单名跳转」;
     * 跨源环境(本地 dev)内部自动降级为纯文本。
     */
    onFrameLoad() {
      if (this.aiJumpCleanup) {
        this.aiJumpCleanup()
        this.aiJumpCleanup = null
      }
      this.aiJumpCleanup = installAiJump(this.$refs.frame, {
        onJump: path => this.jumpToMenu(path)
      })
    },
    /** 跳转到回答里点中的菜单页并收起抽屉 (iframe 保活销毁由 v-if 语义处理, 会话由 MaxKB 侧恢复) */
    jumpToMenu(path) {
      this.drawerVisible = false
      this.$router.push(path).catch(() => {})
    },
    onResize() {
      this.isMobile = window.innerWidth < 768
      this.ballTop = this.clampTop(this.ballTop)
    }
  }
}
</script>

<style scoped>
.ai-helper-wrap {
  /* 组件根不占位, 全部 fixed */
  position: static;
}
/* ---------- 小球 ---------- */
.ai-ball {
  position: fixed;
  right: 0;
  /* top 由内联样式控制 (可拖动, localStorage 记忆) */
  z-index: 1000;
  width: 58px;
  height: 72px;
  border-radius: 26px 0 0 26px;
  background: linear-gradient(135deg, #5baa46 0%, #3f8a34 100%);
  box-shadow: -3px 4px 14px rgba(63, 138, 52, 0.45);
  cursor: grab;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  user-select: none;
  animation: ai-breathe 3s ease-in-out infinite;
  transition: transform 0.2s ease, width 0.2s ease;
  touch-action: none;
}
.ai-ball.dragging {
  cursor: grabbing;
  animation-play-state: paused;
  transition: none;
  box-shadow: -3px 6px 20px rgba(63, 138, 52, 0.6);
}
.ai-ball:hover {
  transform: translateX(-4px) scale(1.06);
  animation-play-state: paused;
}
.ai-ball.dragging:hover {
  transform: none;
}
.ai-ball-icon {
  font-size: 20px;
  line-height: 1;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
}
.ai-ball-label {
  font-size: 11px;
  line-height: 1.15;
  color: #fff;
  text-align: center;
  font-weight: 600;
  letter-spacing: 1px;
}
@keyframes ai-breathe {
  0%, 100% { box-shadow: -3px 4px 14px rgba(63, 138, 52, 0.4); }
  50% { box-shadow: -3px 4px 22px rgba(63, 138, 52, 0.7); }
}
/* 窄屏小球缩小 */
.is-mobile .ai-ball {
  width: 46px;
  height: 58px;
}
.is-mobile .ai-ball-icon { font-size: 16px; }
.is-mobile .ai-ball-label { font-size: 10px; }

/* ---------- 抽屉 ---------- */
.ai-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  width: min(420px, 92vw);
  background: #fff;
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  border-radius: 0;
}
.ai-drawer-header {
  flex: none;
  height: 56px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #5baa46 0%, #3f8a34 100%);
  color: #fff;
}
.ai-drawer-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ai-drawer-logo {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.ai-drawer-name {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
}
.ai-drawer-env {
  font-size: 11px;
  opacity: 0.85;
  line-height: 1.4;
}
.ai-drawer-close {
  font-size: 18px;
  cursor: pointer;
  padding: 6px;
  opacity: 0.9;
}
.ai-drawer-close:hover { opacity: 1; transform: scale(1.1); }
.ai-drawer-body {
  flex: 1;
  min-height: 0;
  background: #f6f8f5;
}
.ai-drawer-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.ai-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.35);
}

/* ---------- 动画 (CSS animation, 不依赖 transitionend —— 后台标签页也能正确落位) ---------- */
.ai-drawer {
  animation: ai-slide-in 0.26s ease;
}
.ai-mask {
  animation: ai-fade-in 0.25s ease;
}
@keyframes ai-slide-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
@keyframes ai-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
